import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Physical Safeguards
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography>Physical Safeguards</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            This section of the documentation provides a detailed overview of the physical safeguards that are critical
            for ensuring our SaaS medical application's compliance with the Health Insurance Portability and
            Accountability Act (HIPAA). Physical safeguards are essential for protecting the physical systems and the
            buildings that house them, as well as securing the devices that access Protected Health Information (PHI).
            By implementing these safeguards, we demonstrate our commitment to protecting the privacy and security of
            PHI against unauthorized physical access, tampering, and theft.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Overview
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Facility Access and Control:</b> Policies and procedures designed to limit physical access to
                  electronic information systems and the facilities in which they are housed, while ensuring that
                  authorized access is allowed. This includes establishing secure areas, controlling access to these
                  areas, and managing visitors.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Workstation and Device Security:</b> Guidelines and strategies for ensuring that all workstations
                  and devices that access PHI are secure. This covers the use, positioning, and access to workstations
                  and electronic media. Device and media controls, including disposal, re-use, and movement of these
                  devices, are also addressed.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Physical Security Measures:</b> Implementation of physical measures to protect the building and its
                  assets from intrusions, environmental hazards, and unauthorized access. This includes security alarms,
                  door locks, surveillance cameras, and secure disposal or destruction of physical records containing
                  PHI.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Objectives
          </Typography>
          <List>
            <ListItem>
              To ensure comprehensive protection of physical infrastructure from unauthorized access, tampering, and
              theft.
            </ListItem>
            <ListItem>
              To establish and maintain secure environments for information systems that handle PHI, thereby reducing
              the risk of data breaches and ensuring the integrity and confidentiality of patient information.
            </ListItem>
            <ListItem>
              To affirm our ongoing dedication to safeguarding patient health information through stringent physical
              security measures.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
